<template>
  <div ref="popupMenuContainer"
       class="popupMenuContainer"
       :class="{ horizontal }"
       @mouseenter.stop="onMouseEnter"
       @mouseleave.stop="onMouseLeave">
    <component :is="useMenuOpenerButton ? 'button' : 'div'"
               class="menuOpener"
               :class="{ root: isRoot, subMenu: isSubMenu }"
               :disabled="menuDisabled"
               :tabindex="openerTabFocusable ? null : -1"
               ref="menuOpener"
               aria-label="Menu"
               @focusin.self="openerFocused = true"
               @focusout.self="openerFocused = false"
               @click.stop.prevent="toggle">
      <slot name="label"></slot>
      <PopupMenuButton v-if="hasItems && value"
                       :class="(value ? value.cssClass : '')"
                       :value="value"
                       :title="value ? value.elementTitle : ''"
                       :name="name"
                       :icon="icon"
                       :id="value ? value.elementId : ''"
                       :showChevron="showChevron"
                       :showIconFocus="isRoot"
                       :isRoot="isRoot"
                       :isSubMenu="isSubMenu"
                       :horizontal="horizontal"
                       :acceptFocus="!openerTabFocusable"
                       @subMenuToggle="toggle"
                       @subMenuOpen="open"
                       @subMenuClose="close"
                       @itemClicked="selectItem"
                       ref="menuOpenerButton"
                       class="menuOpenerButton">
      </PopupMenuButton>
      <slot name="menuOpener" :menuOpen="show" :openerFocused="openerFocused"></slot>
    </component>
    <div v-if="(hasItems || populated.default) && trapMouse && isRoot && show" class="mouseTrap"
         @click.stop.prevent="close" />
    <menu v-if="show"
          ref="popupMenu"
          class="popupMenu"
          :aria-label="value ? value.elementTitle : null"
          :style="menuStyle"
          :class="{ root: isRoot, subMenu: isSubMenu, transform: !canTriggerSeparatePopup }">
      <div class="pmHeader" v-if="populated.header" @click.stop.prevent>
        <slot name="header"></slot>
      </div>
      <div v-else-if="hasItems && menuHeading">
        <PopupMenuButton ref="itemButtons"
                         class="menuHeading"
                         :title="menuHeading.elementTitle"
                         :id="menuHeading.elementId"
                         :class="menuHeading.cssClass"
                         :value="menuHeading"
                         :horizontal="horizontal">
        </PopupMenuButton>
      </div>
      <div class="pmBody menuSection" v-if="populated.default" @click="handleBodyClick">
        <slot name="default"></slot>
      </div>
      <div class="pmBody menuSection" v-if="hasItems" :class="{ itemSelected: hasSelectedItem }">
        <template v-for="(item, index) of items">
          <PopupMenuButton v-if="item.itemType === 'item' ||
                             item.itemType === 'item_menu' ||
                             item.itemType === 'item_hybrid'"
                           ref="itemButtons"
                           :title="item.elementTitle"
                           :id="item.elementId"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :parentValue="value"
                           :isSubMenu="isSubMenu"
                           :horizontal="horizontal"
                           :disabled="item.disabled"
                           @subMenuToggle="toggle"
                           @subMenuOpen="open"
                           @subMenuClose="close"
                           @itemClicked="selectItem" />
          <PopupMenuButton v-if="item.itemType === 'menu'"
                           ref="itemButtons"
                           :title="item.elementTitle"
                           :id="item.elementId"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :parentValue="value"
                           :isSubMenu="isSubMenu"
                           :horizontal="horizontal"
                           :disabled="item.disabled"
                           @subMenuToggle="toggle"
                           @subMenuOpen="open"
                           @subMenuClose="close"
                           @itemClicked="selectItem" />
          <PopupMenuButton v-if="item.itemType === 'action'"
                           ref="itemButtons"
                           :title="item.elementTitle"
                           :id="item.elementId"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :parentValue="value"
                           :horizontal="horizontal"
                           :disabled="item.disabled"
                           @subMenuToggle="toggle"
                           @subMenuOpen="open"
                           @subMenuClose="close" />
          <PopupMenuButton v-if="item.itemType === 'in_menu_action'"
                           ref="itemButtons"
                           :title="item.elementTitle"
                           :id="item.elementId"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :parentValue="value"
                           :horizontal="horizontal"
                           @subMenuToggle="toggle"
                           @subMenuOpen="open"
                           @subMenuClose="close" />
          <PopupMenuButton v-if="item.itemType === 'url'"
                           ref="itemButtons"
                           :title="item.elementTitle"
                           :id="item.elementId"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :horizontal="horizontal"
                           :disabled="item.disabled"
                           @subMenuToggle="toggle"
                           @subMenuOpen="open"
                           @subMenuClose="close" />
          <PopupMenuButton v-if="item.itemType === 'text'"
                           :key="`${item.name}__${index}`"
                           :class="item.cssClass"
                           :value="item"
                           :horizontal="horizontal"
                           :disabled="item.disabled" />
          <PopupMenuDivider v-if="item.itemType === 'divider'"
                            :key="`${item.name}__${index}`"
                            :class="item.cssClass" />
        </template>
      </div>
      <div class="pmFooter menuSection" v-if="populated.footer" @click.stop.prevent>
        <slot name="footer"></slot>
      </div>
    </menu>
  </div>
</template>

<script setup lang="ts">
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  useSlots,
  watch,
} from 'vue';

import PopupMenuButton, { PopupMenuButtonApi } from '@/components/dialogs/popupmenu/PopupMenuButton.vue';
import PopupMenuDivider from '@/components/dialogs/popupmenu/PopupMenuDivider.vue';
import { PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import browserDetection from '@/data/BrowserDetection';
import { isSlotPopulated } from '@/data/helpers/ComponentHelper';
import { isEditableElementSelected } from '@/data/helpers/DomHelper';
import { randomElementId } from '@/data/helpers/WrapperComponentHelper';
import appEventBus from '@/eventbus/EventBus';
import { useUIStateStore } from '@/stores/UIState';

type Rectangle<T> = {
  top: T,
  right: T,
  bottom: T,
  left: T
}

type MenuStyleRectangle = Partial<Rectangle<string>>;

type PaddingOffsetsRectangle = Partial<Rectangle<number>>;

export type HorizontalPositionType = 'auto' | 'centre' | 'right' | 'left';
export type VerticalPositionType = 'auto' | 'centre' | 'above' | 'below';

const uiStateStore = useUIStateStore();
const slots = useSlots();

interface Props {
  id?: string;
  isRoot?: boolean;
  value?: PopupMenuItem;
  name?: string;
  icon?: string;
  openMenusOnHover?: boolean;
  horizontal?: boolean;
  menuDisabled?: boolean;
  openerTabFocusable?: boolean;
  useMenuOpenerButton?: boolean;
  showChevron?: boolean;
  updatePositionOnItemsChanged?: boolean;
  useCapture?: boolean;
  // Preferences for initial constraint of where the menu 'hangs' relative to the element that is used to open it
  horizontalPosition?: HorizontalPositionType;
  verticalPosition?: VerticalPositionType;
  /**
   * If trapMouse = true a fullscreen div will prevent mouse out when selecting from menus deeper than first level.
   * This prevent premature closure of menu
   */
  trapMouse?: boolean;
  items?: PopupMenuItem[] | null;
  // Used where on screen placement is determined externally to PopupMenu
  externalTargetRect?: DOMRect | null;
  // The transform CSS turns the menu into a containing element for fixed-position children,
  // but we don't want that if our menu items can trigger their own separate popup menus
  canTriggerSeparatePopup?: boolean;
  preventBodyClick?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: randomElementId('menu'),
  isRoot: true,
  openMenusOnHover: true,
  horizontal: false,
  menuDisabled: false,
  openerTabFocusable: true,
  useMenuOpenerButton: true,
  showChevron: false,
  updatePositionOnItemsChanged: true,
  useCapture: true,
  horizontalPosition: 'right',
  verticalPosition: 'below',
  trapMouse: false,
  items: null,
  externalTargetRect: null,
  canTriggerSeparatePopup: false,
  preventBodyClick: true,
});

interface Emits {
  (e: 'change', item: PopupMenuItem): void,
  (e: 'subMenuClose', closeAll: boolean): void,
  (e: 'cancel'): void,
  (e: 'open'): void,
}
const emit = defineEmits<Emits>();

const itemButtons: Ref<PopupMenuButtonApi[]> = ref([]);
const menuOpener: Ref<HTMLButtonElement | null> = ref(null);
const menuOpenerButton: Ref<PopupMenuButtonApi | null> = ref(null);
const popupMenuContainer: Ref<HTMLDivElement | null> = ref(null);
const popupMenu: Ref<HTMLDivElement | null> = ref(null);

const focusIndex: Ref<number> = ref(0);
const previousActiveMenuId: Ref<string | null> = ref(null);
const show: Ref<boolean> = ref(false);
const menuStyle: Ref<MenuStyleRectangle> = ref({});

const openerFocused: Ref<boolean> = ref(false);

// Offsets for distance from edges for subMenu positioning
const paddingOffsets: Ref<PaddingOffsetsRectangle> = ref({ top: 8, right: 8, bottom: 8, left: 8 });

const isSubMenu: Ref<boolean> = ref(false);
const menuOnEnterTimeout: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
const menuOnLeaveTimeout: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
const populated: Ref<Record<string, boolean>> = ref({
  header: false,
  default: false,
  footer: false,
});

function emitChange(item: PopupMenuItem): PopupMenuItem {
  emit('change', item);
  return item;
}

const hasItems: ComputedRef<boolean> = computed(() => {
  return !!props.items?.length;
});

const hasSelectedItem: ComputedRef<boolean> = computed(() => {
  return props.items?.find((item) => item.selected === true) !== undefined;
});

const menuHeading: ComputedRef<PopupMenuItem | undefined> = computed(() => {
  return props.items?.find((item) => item.itemType === 'menu_heading');
});

function focusMenuOpenerButton(): void {
  menuOpenerButton.value?.focus();
}

/**
 * Calculate the position of the menu depending on it's location in the window and any constraints re: placement
 */
function updateMenuStyle(): void {
  const targetRect: DOMRect | undefined = props.externalTargetRect ?? menuOpener.value?.getBoundingClientRect();
  const numericStyleValues: Record<string, number> = {};
  const menuRect: DOMRect | undefined = popupMenu.value?.getBoundingClientRect();
  const docRect: DOMRect | undefined = document.body.getClientRects()[0];
  if (!targetRect || !docRect || !menuRect) {
    menuStyle.value = {};
    return;
  }
  const targetCentre = {
    x: targetRect.x + (targetRect.width / 2),
    y: targetRect.y + (targetRect.height / 2),
  };
  const menuFullWidth: number = (paddingOffsets.value.left ?? 0) +
    (paddingOffsets.value.right ?? 0) +
    menuRect.width;
  const menuFullHeight: number = (paddingOffsets.value.top ?? 0) +
    (paddingOffsets.value.bottom ?? 0) +
    menuRect.height;
  if (!isSubMenu.value) {
    switch (props.horizontalPosition) {
      case 'auto':
        if (targetCentre.x < docRect.width / 2) {
          if ((targetRect.left + menuRect.width + (paddingOffsets.value.right ?? 0)) >
            docRect.width - (paddingOffsets.value.right ?? 0)) {
            numericStyleValues.left = docRect.width - (menuRect.width + (paddingOffsets.value.right ?? 0));
          } else {
            numericStyleValues.left = Math.max(targetRect.left, (paddingOffsets.value.left ?? 0));
          }
        } else {
          if (targetRect.right - (menuRect.width + (paddingOffsets.value.left ?? 0)) <
            (paddingOffsets.value.left ?? 0)) {
            numericStyleValues.right = docRect.width - ((paddingOffsets.value.left ?? 0) + menuRect.width);
          } else {
            numericStyleValues.right = Math.max(docRect.width - targetRect.right, (paddingOffsets.value.right ?? 0));
          }
        }
        break;
      case 'centre' :
        if (targetCentre.x - (menuFullWidth / 2) < (paddingOffsets.value.left ?? 0) ||
          targetCentre.x + (menuFullWidth / 2) > docRect.width - (paddingOffsets.value.right ?? 0)) {
          numericStyleValues.left = (paddingOffsets.value.left ?? 0);
        } else {
          numericStyleValues.left = targetCentre.x - (menuFullWidth / 2);
        }
        break;
      case 'right' :
        if ((targetRect.left + menuRect.width + (paddingOffsets.value.right ?? 0)) >
          docRect.width - (paddingOffsets.value.right ?? 0)) {
          numericStyleValues.left = docRect.width - (menuRect.width + (paddingOffsets.value.right ?? 0));
        } else {
          numericStyleValues.left = Math.max(targetRect.left, (paddingOffsets.value.left ?? 0));
        }
        break;
      case 'left' :
        if (targetRect.right - (menuRect.width + (paddingOffsets.value.left ?? 0)) < (paddingOffsets.value.left ?? 0)) {
          numericStyleValues.right = docRect.width - ((paddingOffsets.value.left ?? 0) + menuRect.width);
        } else {
          numericStyleValues.right = Math.max(docRect.width - targetRect.right, (paddingOffsets.value.right ?? 0));
        }
        break;
    }

    switch (props.verticalPosition) {
      case 'auto' :
        if (targetRect.bottom + menuRect.height >= docRect.bottom) {
          numericStyleValues.top = targetRect.top - menuRect.height;
        } else {
          numericStyleValues.top = targetRect.bottom;
        }
        break;
      case 'centre' :
        numericStyleValues.top = targetCentre.y - (menuFullHeight / 2);
        break;
      case 'above' :
        numericStyleValues.top = targetRect.top - menuRect.height;
        break;
      case 'below' :
        numericStyleValues.top = targetRect.bottom;
        break;
    }
    if (numericStyleValues.top > docRect.height - menuFullHeight) {
      numericStyleValues.top = docRect.height - (menuRect.height + (paddingOffsets.value.top ?? 0));
    } else if (numericStyleValues.top < 0) {
      numericStyleValues.top = (paddingOffsets.value.top ?? 0);
    }
  } else {
    if (popupMenuContainer.value && menuOpener.value) {
      const parentRect: DOMRect | undefined = menuOpener.value?.offsetParent?.getBoundingClientRect();
      const leftSpaceAvailable: number = targetRect.x;
      const rightSpaceAvailable: number = docRect.width - targetRect.right;
      if (leftSpaceAvailable > rightSpaceAvailable) {
        numericStyleValues.left = Math.max(-(leftSpaceAvailable - (paddingOffsets.value.left ?? 0)), -menuRect.width);
      } else {
        numericStyleValues.left = Math.min((docRect.width - targetRect.left) -
          (menuRect.width + (paddingOffsets.value.right ?? 0)), targetRect.width);
      }
      const topSpaceAvailable: number = targetRect.bottom;
      const bottomSpaceAvailable: number = docRect.height - targetRect.top;
      let topOffset: number = 0;
      if (topSpaceAvailable > bottomSpaceAvailable) {
        const menuHeightPlusPaddingTop: number = (menuRect.height + (paddingOffsets.value.top ?? 0));
        topOffset = topSpaceAvailable - menuHeightPlusPaddingTop < 0
          ? menuHeightPlusPaddingTop - topSpaceAvailable : 0;
        numericStyleValues.top = (targetRect.bottom - (parentRect?.top ?? 0)) - (menuRect.height) + topOffset;
      } else {
        const menuHeightPlusPaddingBottom: number = (menuRect.height + (paddingOffsets.value.bottom ?? 0));
        topOffset = bottomSpaceAvailable - menuHeightPlusPaddingBottom < 0
          ? menuHeightPlusPaddingBottom - bottomSpaceAvailable : 0;
        numericStyleValues.top = targetRect.top - (topOffset + (parentRect?.top ?? 0));
      }
    }
  }
  const tempStyle: Record<string, string> = {};
  for (const prop of Object.keys(numericStyleValues)) {
    tempStyle[prop] = numericStyleValues[prop] + 'px';
  }
  menuStyle.value = tempStyle;
}

/**
 * Respond to 'global' click event - determining whether menu should close
 */
function clickListener(event: Event): void {
  if (!event.composedPath().includes(popupMenuContainer.value as EventTarget)) {
    close();
  }
}

function toggle(): void {
  if (show.value) {
    close();
  } else {
    open();
  }
}

function open(): void {
  document.addEventListener('keydown', navigationKeyPress, props.useCapture);
  window.addEventListener('mousedown', clickListener, props.useCapture);
  window.addEventListener('wheel', clickListener, props.useCapture);
  window.addEventListener('resize', onResize, props.useCapture);
  show.value = true;
}

function close(closeAll: boolean = false): void {
  emit('subMenuClose', closeAll);
  show.value = false;
  document.removeEventListener('keydown', navigationKeyPress, props.useCapture);
  window.removeEventListener('mousedown', clickListener, props.useCapture);
  window.removeEventListener('wheel', clickListener, props.useCapture);
  window.removeEventListener('resize', onResize, props.useCapture);
}

// we may want to close the PopupMenu but have it respond differently to the standard close
function cancel(): void {
  emit('cancel');
  show.value = false;
  document.removeEventListener('keydown', navigationKeyPress, props.useCapture);
  window.removeEventListener('mousedown', clickListener, props.useCapture);
  window.removeEventListener('wheel', clickListener, props.useCapture);
  window.removeEventListener('resize', onResize, props.useCapture);
}

function onMouseEnter(): void {
  if (menuOnLeaveTimeout.value) {
    clearTimeout(menuOnLeaveTimeout.value);
    menuOnLeaveTimeout.value = null;
  }
  if (props.openMenusOnHover && !props.isRoot) {
    if (!menuOnEnterTimeout.value) {
      menuOnEnterTimeout.value = setTimeout(() => {
        open();
      }, 250);
    }
  }
}

function onMouseLeave(): void {
  if (menuOnEnterTimeout.value) {
    clearTimeout(menuOnEnterTimeout.value);
    menuOnEnterTimeout.value = null;
  }
  if (props.openMenusOnHover && !props.isRoot) {
    if (!menuOnLeaveTimeout.value) {
      menuOnLeaveTimeout.value = setTimeout(() => {
        close(false);
      }, 250);
    }
  }
}

function selectItem(value: PopupMenuItem): void {
  const foundIndex = itemButtons.value?.findIndex(button => button.getValue() === value);
  if (foundIndex) {
    focusIndex.value = foundIndex;
  }
  emitChange(value);
  close(true);
}

function navigationKeyPress(event: KeyboardEvent): void {
  if (!show.value) {
    return;
  }

  if (event.key === 'Escape') {
    event.stopPropagation();
    cancel();
  }

  if (!hasItems.value) {
    // Use native keypress handling
    return;
  }

  if (!props.horizontal) {
    if (isEditableElementSelected()) {
      return;
    }
    switch (event.key) {
      case 'Tab' :
      case 'ArrowDown' :
        if (itemButtons.value && uiStateStore.activePopupMenuId === props.id) {
          focusIndex.value = focusIndex.value === itemButtons.value.length - 1 ? 0 : focusIndex.value + 1;
          if (itemButtons.value[focusIndex.value]) {
            event.preventDefault();
            itemButtons.value[focusIndex.value].focus();
          }
        }
        break;
      case 'ArrowUp':
        if (itemButtons.value && uiStateStore.activePopupMenuId === props.id) {
          focusIndex.value = focusIndex.value === 0 ? itemButtons.value.length - 1 : focusIndex.value - 1;
          if (itemButtons.value[focusIndex.value]) {
            event.preventDefault();
            itemButtons.value[focusIndex.value].focus();
          }
        }
        break;
      case 'Escape' :
        event.preventDefault();
        show.value = false;
        break;
    }
  } else {
    switch (event.key) {
      case 'Tab' :
      case 'ArrowRight' :
        event.preventDefault();
        if (itemButtons.value && uiStateStore.activePopupMenuId === props.id) {
          focusIndex.value = focusIndex.value === itemButtons.value.length - 1 ? 0 : focusIndex.value + 1;
          itemButtons.value[focusIndex.value].focus();
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (itemButtons.value && uiStateStore.activePopupMenuId === props.id) {
          focusIndex.value = focusIndex.value === 0 ? itemButtons.value.length - 1 : focusIndex.value - 1;
          itemButtons.value[focusIndex.value].focus();
        }
        break;
      case 'Escape' :
        event.preventDefault();
        show.value = false;
        break;
    }
  }
}

function onMenuChangeFocusButton(): void {
  uiStateStore.setActivePopupMenuId(props.id);
  if (focusIndex.value > -1) {
    if (props.isRoot) {
      itemButtons.value?.[focusIndex.value]?.focus();
    } else {
      // Wait for the submenu to open
      nextTick(() => {
        itemButtons.value?.[focusIndex.value]?.focus();
      });
    }
  } else {
    if (props.isRoot) {
      itemButtons.value?.forEach(button => button.blur());
    } else {
      nextTick(() => {
        itemButtons.value?.forEach(button => button.blur());
      });
    }
  }
}

function onResize(): void {
  // On a mobile device this will probably be the keyboard opening
  // (or rotation which isn't supported anyway) so don't close the menu
  if (!browserDetection.isMobileOrTablet()) {
    close();
  }
}

function handleActiveMenuHidden(previousMenuId: string): void {
  // Currently active popup menu has been hidden - check if this was the previously active popup menu
  if (previousMenuId === props.id) {
    onMenuChangeFocusButton();
  }
}

function handleBodyClick(event: MouseEvent): void {
  if (props.preventBodyClick) {
    event.stopPropagation();
    event.preventDefault();
  }
}

watch(() => props.items, () => {
  if (props.updatePositionOnItemsChanged) {
    nextTick(() => {
      updateMenuStyle();
    });
  }
});

watch(() => show.value, (newValue: boolean) => {
  focusIndex.value = -1;
  if (newValue) {
    nextTick(() => {
      emit('open');
      populated.value.header = isSlotPopulated(slots.header);
      populated.value.default = isSlotPopulated(slots.default);
      populated.value.footer = isSlotPopulated(slots.footer);
      // Wait for the subMenu to be rendered to the DOM before calculating positional style
      updateMenuStyle();
      // Remember the last menu to be active for return from subMenu
      previousActiveMenuId.value = uiStateStore.activePopupMenuId;
      // focus the correct item in the previous menu on return from subMenu
      onMenuChangeFocusButton();
    });
  } else {
    if (previousActiveMenuId.value) {
      appEventBus.emit('activePopupMenuHidden', { previousMenuId: previousActiveMenuId.value });
    }
  }
  if (props.isRoot) {
    uiStateStore.setPopupMenuIsVisible(newValue);
  }
});

watch(() => props.externalTargetRect, () => {
  updateMenuStyle();
});

onMounted(() => {
  nextTick(() => {
    populated.value.header = isSlotPopulated(slots.header) ?? false;
    populated.value.default = isSlotPopulated(slots.default) ?? false;
    populated.value.footer = isSlotPopulated(slots.footer) ?? false;
  });
  appEventBus.on('activePopupMenuHidden', handleActivePopupMenuHidden);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', navigationKeyPress, props.useCapture);
  window.removeEventListener('mousedown', clickListener, props.useCapture);
  window.removeEventListener('wheel', clickListener, props.useCapture);
  window.removeEventListener('resize', onResize, props.useCapture);
  appEventBus.off('activePopupMenuHidden', handleActivePopupMenuHidden);
  if (props.isRoot) {
    uiStateStore.setPopupMenuIsVisible(false);
  }
});

function handleActivePopupMenuHidden(event: {previousMenuId: string}): void {
  handleActiveMenuHidden(event.previousMenuId);
}

isSubMenu.value = !props.isRoot;

export interface PopupMenuApi extends ComponentPublicInstance {
  popupMenuContainer: HTMLDivElement | null;
  open: () => void;
  close: (closeAll?: boolean) => void;
  toggle: () => void;
  cancel: () => void;
  focusMenuOpenerButton: () => void;
  updateMenuStyle: () => void;
}

defineExpose({
  popupMenuContainer,
  open,
  close,
  toggle,
  cancel,
  focusMenuOpenerButton,
  updateMenuStyle,
});
</script>

<style scoped lang="scss">
.popupMenuContainer {
  display: contents;
  .mouseTrap {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    cursor: initial;
  }
  button {
  &.disabled {
    :deep(.brandIconContainer) {
      svg.icon {
        color: currentColor;
      }
    }
  }
  }
  .menuOpener {
  // We need to render this over the rest of the app to capture clicks. We can't use a portal as we want a natural
  // tab order for popup menus, where keyboard nav tabs to the menu straight after the trigger to open it.
    display: flex;
    flex: 1;
    max-width: 100%;
    border-radius: 0;
    border: none;
    &.root > .menuOpenerButton {
      padding: 0;
    }
    &.root {
      flex: 0;
      height: fit-content;
    }
  }
}

.popupMenu {
  isolation: isolate;
  z-index: $z-layer-popups;
  position: fixed;
  cursor: default;
  margin: 0;
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  border-radius: $size-border-radius-modal;
  background: $colour-background-light;
  box-shadow: 0 0 0 1px $colour-border-mid, 0 0 1rem rgba(0, 0, 0, 0.2);
  max-height: calc(100 * var(--vh, 1vh) - 2rem);
  max-width: calc(100vw - 1rem);
  &.transform {
    // We need to break it out of any 'overflow: hidden' rules by hacking a translate step in
    transform: translate(0);
  }
  .menuHeading {
    cursor: default;
    pointer-events: none;
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.25rem;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    background: $colour-background-mid-light;
    border-bottom: 1px solid $colour-border-mid-light;
  }
  .pmHeader {
    border-bottom: 1px solid $colour-border-light;
    font-weight: $weight-bold;
    padding: 1rem 1.75rem;
    font-size: 1rem;
  }
  .pmFooter {
    border-top: 1px solid $colour-border-light;
  }
  .pmHeader {
    padding: 0.5rem 1rem;
    font-size: $size-font-field-label;
    background: $colour-background-mid-light;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75ch;
    margin: -0.5rem 0 0.25rem;
    border-bottom: 1px solid $colour-border-mid-light;
    border-radius: 0.5rem 0.5rem 0 0;
    .menuHeading {
      font-weight: $weight-semi-bold;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .headingAdditionalText {
      font-weight: $weight-normal;
      white-space: nowrap;
    }
  }
}

.pmBody {
  &.menuSection {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    .horizontal.popupMenuContainer & {
      overflow-y: hidden;
      overflow-x: auto;
      flex-direction: row;
    }
  }
}

ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.popupMenuButton {
  &.emptyFolder {
    pointer-events: none;
    color: $colour-icon-menu-submenu;
    .buttonLabel {
      color: $colour-icon-menu-submenu;
      font-style: italic;
    }
  }
  &.integration {
    .brandIconContainer {
      margin-right: 0.25rem;
    }
  }
  .menuSection & {
    min-width: 10rem;
  }
}
</style>
